import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';
import type { Product } from '../../types/order';
import { formatMoney } from '../../utils/formatMoney';
import { weightedLineTotal } from '../../utils/pricing';

interface OpenPriceModalProps {
  product: Product;
  onConfirm: (product: Product, price: number) => void;
  onClose: () => void;
  /**
   * «Βάρος + τιμή κιλού» (utils/weightedOpenPrice): το βάρος που μόλις δόθηκε. Τότε το ποσό είναι
   * ΤΙΜΗ ΚΙΛΟΥ (ή λίτρου, με weightUnit 'LT') και φαίνεται το σύνολο βάρος × τιμή.
   */
  weight?: number | null;
  weightUnit?: string;
}

const OpenPriceModal: React.FC<OpenPriceModalProps> = ({ product, onConfirm, onClose, weight = null, weightUnit }) => {
  const { t } = useTranslation(['products', 'common']);
  const [priceInput, setPriceInput] = useState<string>('');
  const perUnitWeight = weight !== null && weight > 0 ? weight : null;
  const perUnit = perUnitWeight !== null;
  const unitLabel = weightUnit === 'LT' ? 'lt' : 'kg';

  const handleKeyPress = (key: string) => {
    if (key === '←') {
      setPriceInput(prev => prev.slice(0, -1));
    } else if (key === '.') {
      if (!priceInput.includes('.')) {
        setPriceInput(prev => prev + '.');
      }
    } else {
      const parts = priceInput.split('.');
      if (parts[1] && parts[1].length >= 2) return;
      setPriceInput(prev => prev + key);
    }
  };

  const handleConfirm = () => {
    const price = parseFloat(priceInput);
    if (price > 0) {
      onConfirm(product, price);
    }
  };

  const isValid = priceInput !== '' && parseFloat(priceInput) > 0;

  return (
    <div 
      className="cashier-overlay fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        data-testid="open-price-modal"
        className="cashier-dialog bg-surface-1 rounded-2xl p-5 max-w-sm w-full shadow-2xl border border-emerald-300 dark:border-emerald-800/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-t1">{product.name}</h3>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              {perUnit ? t(weightUnit === 'LT' ? 'pricePerLitreEntry' : 'pricePerKgEntry') : t('priceEntry')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl bg-surface-3 hover:bg-surface-4 flex items-center justify-center"
          >
            <X className="h-5 w-5 text-t1" />
          </button>
        </div>

        {/* Price Display */}
        <div className="bg-surface-2 rounded-2xl p-4 mb-4">
          <div className="text-center">
            <div className="text-4xl font-bold text-t1">
              €{priceInput || '0.00'}{perUnit && <span className="text-2xl text-t3">/{unitLabel}</span>}
            </div>
            {perUnitWeight !== null && (
              <div data-testid="open-price-weight-total" className="text-lg text-blue-600 dark:text-blue-400 font-semibold mt-1">
                {perUnitWeight.toFixed(3).replace('.', ',')} {unitLabel} = {formatMoney(weightedLineTotal(Number(priceInput || 0), perUnitWeight))}
              </div>
            )}
          </div>
        </div>

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '←'].map((key) => (
            <button
              key={key}
              onClick={() => handleKeyPress(key)}
              className={`h-14 rounded-xl font-bold text-xl transition-all active:scale-95 ${
                key === '←' 
                  ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/60' 
                  : 'bg-surface-3 text-t1 hover:bg-surface-4'
              }`}
            >
              {key}
            </button>
          ))}
        </div>

        {/* Quick Price Buttons */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[1, 2, 5, 10].map((price) => (
            <button
              key={price}
              onClick={() => setPriceInput(price.toFixed(2))}
              className="py-3 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 font-bold text-sm hover:bg-emerald-200 dark:hover:bg-emerald-900/60 active:scale-95 transition-all"
            >
              €{price}
            </button>
          ))}
        </div>

        {/* Add Button */}
        <button
          onClick={handleConfirm}
          disabled={!isValid}
          className="w-full h-14 rounded-xl bg-green-600 hover:bg-green-700 disabled:bg-surface-3 disabled:text-t4 disabled:cursor-not-allowed text-white font-bold text-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          <Plus className="h-5 w-5" />
          {t('common:add')}
        </button>
      </div>
    </div>
  );
};

export default OpenPriceModal;
